/**
 * Rollen & Rechte, Etappe 8 „Nachbereitung, Abrechnung, Auswertung“: die Formeln der Wege — rein (nur `rechte.ts`), keine Datenbank, keine Uhr.
 *
 * Liegt doppelt in Backend und Frontend (`utils/nachbereitung.ts`), ein Test vergleicht Byte für Byte (doppelteDateien.test.ts). Der Server
 * prüft mit diesen Formeln, die Oberfläche zeigt Knöpfe mit denselben — „Knopf ⇔ Server“ ohne zweite Rechnung.
 *
 * Grundsätze (docs/rollen-rechte.md, „Was Etappe 8 kann“):
 *  - Lesende Wege der Nachbereitung hängen an der Funktion (`nAbrechnung`, `nExport`, `nBackup`), schreibende zusätzlich an „Grunddaten
 *    bearbeiten“ (`eBearbeiten`): `nAbrechnung`, `nExport` und `nBackup` stehen nicht in `SCHREIB_FUNKTIONEN` und können einem Mitbearbeiter
 *    „Betrachten“ einzeln gegeben werden — Ändern, Anlegen und Löschen dürfen daraus nicht folgen.
 *  - „Feste Regel ODER Funktion“ (öffentliches PDF, öffentlicher Link): Die feste Regel (Ersteller, Mitbearbeiter) gilt nur, solange am Konto
 *    kein Verbot der Funktion steht (`hatVerbot`) — sonst wäre „ein Verbot gilt auf allen Wegen“ gebrochen.
 *  - „Feste Regel UND Funktion“ (LES-Benutzer-CSV): Administration und Ereignisverantwortliche, und die Funktion darf nicht verboten sein.
 *  - Monatsabschluss und Auswertung rechnen über ALLE Ereignisse: Sie wirken nur zusammen mit „Ereignisse ansehen: überall“.
 */
import { darf, hatVerbot, helferFeldMaske, ueberallWirksam, RechteKontext, RechteEreignis } from './rechte';

/** Feste Rollen, die die LES-Benutzer-Datei erstellen dürfen (Schnittstelle der Kreisverbandsebene, Entscheidung E8 F2 b) */
const LES_ROLLEN: readonly string[] = ['SUPER_ADMIN', 'ADMIN', 'EREIGNISVERANTWORTLICHER'];

// ---------- Abrechnung ----------

/** Abrechnung ansehen (Sätze, Auszahlungsziele, Zuschläge, Belegart, Pauschalen, Empfänger, Sperren) */
export const darfAbrechnung = (k: RechteKontext, e: RechteEreignis): boolean => darf(k, 'nAbrechnung', e);

/** Abrechnung ändern: zusätzlich „Grunddaten bearbeiten“ (E8 F4 a) */
export const darfAbrechnungAendern = (k: RechteKontext, e: RechteEreignis): boolean => darf(k, 'nAbrechnung', e) && darf(k, 'eBearbeiten', e);

/** Abrechnungs-Exporte (PDF, CSV, Zahlungsanweisungen, Formulare, Komplett-Export): Abrechnung UND Exporte (N3) */
export const darfAbrechnungExport = (k: RechteKontext, e: RechteEreignis): boolean => darf(k, 'nAbrechnung', e) && darf(k, 'nExport', e);

// ---------- Exporte, Links ----------

/** Dienstplan-PDF, Verpflegung, Ortsverein, HRT: „Exporte und PDF“ */
export const darfExport = (k: RechteKontext, e: RechteEreignis): boolean => darf(k, 'nExport', e);

const istErsteller = (k: RechteKontext, e: RechteEreignis): boolean => e.createdById === k.userId;
const istMitbearbeiter = (k: RechteKontext, e: RechteEreignis): boolean => Array.isArray(e.collaborators) && e.collaborators.some(c => c.userId === k.userId);

/** Der feste Weg zu einer Export-Funktion, solange kein Verbot der Funktion am Konto steht und das Ereignis sichtbar ist */
const festerWeg = (k: RechteKontext, e: RechteEreignis, weg: boolean): boolean => weg && !hatVerbot(k, 'nExport') && darf(k, 'eAnsehen', e);

/** Fester Weg zum öffentlichen PDF: Ersteller und jeder Mitbearbeiter (ohne Verbot von „Exporte und PDF“) — die Middleware nimmt ihn als Zusatzweg */
export const festerWegOeffentlichesPdf = (k: RechteKontext, e: RechteEreignis): boolean => festerWeg(k, e, istErsteller(k, e) || istMitbearbeiter(k, e));

/** Fester Weg zum öffentlichen Link: der Ersteller (ohne Verbot von „Exporte und PDF“) */
export const festerWegLink = (k: RechteKontext, e: RechteEreignis): boolean => festerWeg(k, e, istErsteller(k, e));

/** Öffentliches PDF (ohne Telefonnummern): „Exporte und PDF“, oder — wie bisher — Ersteller und jeder Mitbearbeiter (ohne Verbot) */
export const darfOeffentlichesPdf = (k: RechteKontext, e: RechteEreignis): boolean => darf(k, 'nExport', e) || festerWegOeffentlichesPdf(k, e);

/** Öffentlichen Link ansehen: „Exporte und PDF“ oder Ersteller (ohne Verbot) */
export const darfLinkSehen = (k: RechteKontext, e: RechteEreignis): boolean => darf(k, 'nExport', e) || festerWegLink(k, e);

/** Öffentlichen Link anlegen oder abschalten: („Exporte und PDF“ UND „Grunddaten bearbeiten“) oder Ersteller (ohne Verbot) */
export const darfLinkVerwalten = (k: RechteKontext, e: RechteEreignis): boolean =>
  (darf(k, 'nExport', e) && darf(k, 'eBearbeiten', e)) || festerWegLink(k, e);

/** Struktur-Link anlegen oder abschalten: „Exporte und PDF“ UND „Grunddaten bearbeiten“ */
export const darfStrukturLinkVerwalten = (k: RechteKontext, e: RechteEreignis): boolean => darf(k, 'nExport', e) && darf(k, 'eBearbeiten', e);

/** LES-Benutzer-Datei und LES-Status: feste Rolle (Administration, Ereignisverantwortliche) UND „Exporte und PDF“ */
export const darfLes = (k: RechteKontext, e: RechteEreignis, rolle: string): boolean => LES_ROLLEN.includes(rolle) && darf(k, 'nExport', e);

/** Die Datei der LES-Benutzer trägt E-Mail-Adressen: zusätzlich das Recht auf E-Mail-Adressen in Listen (`kontaktListe`) */
export const kontaktListeFuer = (k: RechteKontext, e: RechteEreignis): boolean => helferFeldMaske(k, e).kontaktListe;

/** Dateien des Ereignis-Backups herunterladen: volle Feldmaske — das Backup enthält Bank, Adresse, Allergien und Notizen aller Helfenden (E8 F3) */
const volleMaske = (k: RechteKontext, e: RechteEreignis): boolean => darf(k, 'hSensibel', e) && darf(k, 'hBank', e) && darf(k, 'hNotiz', e);

// ---------- Ereignis-Backup ----------

/** Liste, Anlegen: „Backup“ */
export const darfBackup = (k: RechteKontext, e: RechteEreignis): boolean => darf(k, 'nBackup', e);

/** Backup löschen: „Backup“ UND „Grunddaten bearbeiten“ */
export const darfBackupLoeschen = (k: RechteKontext, e: RechteEreignis): boolean => darf(k, 'nBackup', e) && darf(k, 'eBearbeiten', e);

/** Backup herunterladen: „Backup“ UND volle Feldmaske (Sensibles, Bank, Notizen) */
export const darfBackupLaden = (k: RechteKontext, e: RechteEreignis): boolean => darf(k, 'nBackup', e) && volleMaske(k, e);

/** Backup wiederherstellen: „Backup“, „Grunddaten bearbeiten“, „Plan bearbeiten“, „Helfende einteilen“ UND volle Feldmaske — es schreibt alles zurück */
export const darfBackupWiederherstellen = (k: RechteKontext, e: RechteEreignis): boolean =>
  darf(k, 'nBackup', e) && darf(k, 'eBearbeiten', e) && darf(k, 'pBearbeiten', e) && darf(k, 'pEinteilen', e) && volleMaske(k, e);

// ---------- System: Monat, Auswertung, Kalkulation, Planungsrechner ----------

/** Monatsabschluss, Belege, Empfänger: „Monatsabschluss und Abrechnung“ UND „Ereignisse ansehen: überall“ (rechnet über alle Ereignisse) */
export const darfMonat = (k: RechteKontext): boolean => darf(k, 'bMonat') && ueberallWirksam(k, 'eAnsehen');

/**
 * Helfer-Statistik: kein Verbot der Funktion UND (die Funktion mit „Ereignisse ansehen: überall“ ODER eine Freigabe in der Statistik-Freigabe
 * der Verwaltung). Die Freigabe ist ein zusätzlicher Weg (Entscheidung des Super-Admins); ein Verbot sperrt beide.
 */
export const darfAuswertung = (k: RechteKontext, freigabe: boolean): boolean =>
  !hatVerbot(k, 'bAuswertung') && ((darf(k, 'bAuswertung') && ueberallWirksam(k, 'eAnsehen')) || freigabe);

/** Aufteilung der Abmeldegründe je Person in der Statistik: nur mit „Geburtsdatum, Adresse, Allergien sehen“ überall (E8 F6) */
export const darfAbmeldegruende = (k: RechteKontext): boolean => ueberallWirksam(k, 'hSensibel');

/** Planungsrechner: nie Helfende (feste Regel, #673) UND „Planungsrechner“ */
export const darfPlanungsrechner = (k: RechteKontext, rolle: string): boolean => rolle !== 'HELFER' && darf(k, 'bPlanrechner');

// ---------- Kennzeichen für die Oberfläche ----------

export interface NachbereitungKennzeichen {
  /** Plan-Menü „Abrechnung dieses Ereignisses“ */
  abrechnung: boolean;
  /** Sätze, Zuschlag, Pauschalen, Auszahlungsziel, Belegart ändern */
  abrechnungAendern: boolean;
  /** Abrechnungs-Exporte im Abrechnungsfenster */
  abrechnungExport: boolean;
  /** Kopf-Knopf „Teilen & Export“, Dienstplan-PDF, Verpflegung, Ortsverein, HRT */
  export: boolean;
  /** Öffentliches PDF (ohne Telefonnummern) */
  oeffentlich: boolean;
  /** Reiter „Link“ ansehen */
  link: boolean;
  /** Öffentlichen Link anlegen und abschalten */
  linkVerwalten: boolean;
  /** Struktur-Link anlegen und abschalten */
  strukturLinkVerwalten: boolean;
  /** LES-Benutzer-Datei (mit E-Mail-Adressen) */
  lesCsv: boolean;
  /** LES-Status der Helfenden (Name und Status, keine Adressen) */
  lesStatus: boolean;
  /** Plan-Menü „Backup“, Liste und Anlegen */
  backup: boolean;
  backupLaden: boolean;
  backupWiederherstellen: boolean;
  backupLoeschen: boolean;
}

export const KEINE_NACHBEREITUNG: NachbereitungKennzeichen = {
  abrechnung: false, abrechnungAendern: false, abrechnungExport: false, export: false, oeffentlich: false, link: false, linkVerwalten: false,
  strukturLinkVerwalten: false, lesCsv: false, lesStatus: false, backup: false, backupLaden: false, backupWiederherstellen: false, backupLoeschen: false,
};

/** Alle Wahrheitswerte der Nachbereitung eines Ereignisses auf einmal — einmal berechnen, als einzelne Wahrheitswerte weiterreichen */
export const nachbereitungKennzeichen = (k: RechteKontext | null | undefined, e: RechteEreignis | null | undefined, rolle: string): NachbereitungKennzeichen => {
  if (!k || !e) return KEINE_NACHBEREITUNG;
  const les = darfLes(k, e, rolle);
  return {
    abrechnung: darfAbrechnung(k, e),
    abrechnungAendern: darfAbrechnungAendern(k, e),
    abrechnungExport: darfAbrechnungExport(k, e),
    export: darfExport(k, e),
    oeffentlich: darfOeffentlichesPdf(k, e),
    link: darfLinkSehen(k, e),
    linkVerwalten: darfLinkVerwalten(k, e),
    strukturLinkVerwalten: darfStrukturLinkVerwalten(k, e),
    lesCsv: les && kontaktListeFuer(k, e),
    lesStatus: les,
    backup: darfBackup(k, e),
    backupLaden: darfBackupLaden(k, e),
    backupWiederherstellen: darfBackupWiederherstellen(k, e),
    backupLoeschen: darfBackupLoeschen(k, e),
  };
};
